/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { TriggerScheduleResource, EnvironmentResource, ChannelResource, ScopedDeploymentActionResource, DaysPerMonthTriggerScheduleResource, CronTriggerScheduleResource, DeployLatestReleaseActionResource, DeployNewReleaseActionResource, DeployLatestReleaseToEnvironmentActionResource, TriggerScheduleIntervalResource, RunRunbookActionResource, RunbookResource, OnceDailyTriggerScheduleResource, ContinuousDailyTriggerScheduleResource, TagSetResource, } from "@octopusdeploy/octopus-server-client";
import { TriggerActionType, TriggerScheduleIntervalType, TriggerFilterType } from "@octopusdeploy/octopus-server-client";
import moment from "moment";
import { environmentChipListIncludingMissing, channelChipList, ChipIcon, RunbookChip, ChannelChip } from "~/components/Chips";
import LookupResourceChipComponent from "~/components/LookupResourceChip/LookupResourceChip";
import Tag from "~/components/Tag";
import { hydrateTagsByName } from "~/components/tagSummary";
import { getCronExpressionDescription } from "~/utils/CronExpressions/getCronExpressionDescription";
import DateFormatter from "~/utils/DateFormatter/DateFormatter";
import DaysDescriptionHelper from "./DaysDescriptionHelper";
import DaysPerMonthDescriptionHelper from "./DaysPerMonthDescriptionHelper";
import ScheduleIntervalDescriptionHelper from "./ScheduleIntervalDescriptionHelper";
export default class ScheduledTriggerDescriptionHelper {
    public static getScheduleDescription(schedule: TriggerScheduleResource, emptyText: string = "") {
        try {
            switch (schedule.FilterType) {
                case TriggerFilterType.OnceDailySchedule:
                    return this.getDailyScheduleDescription(schedule as OnceDailyTriggerScheduleResource);
                case TriggerFilterType.ContinuousDailySchedule:
                    return this.getDailyScheduleDescription(schedule as ContinuousDailyTriggerScheduleResource);
                case TriggerFilterType.DaysPerMonthSchedule:
                    return this.getDaysPerMonthScheduleDescription(schedule as DaysPerMonthTriggerScheduleResource, emptyText);
                case TriggerFilterType.CronExpressionSchedule:
                    return this.getCronExpressionScheduleDescription(schedule as CronTriggerScheduleResource, emptyText);
            }
        }
        catch (e) {
            return e;
        }
    }
    public static getActionDescription(action: ScopedDeploymentActionResource | RunRunbookActionResource, environments: EnvironmentResource[], runbooks: RunbookResource[], channels: ChannelResource[], tagSets: TagSetResource[] = [], emptyText: string = "") {
        let preText = null;
        let channelDescription = null;
        let postText = null;
        let sourceEnvironmentIds: string[] = [];
        let destinationEnvironmentId = null;
        let to = "to";
        switch (action.ActionType) {
            case TriggerActionType.DeployLatestRelease:
                preText = "Deploy the latest release";
                sourceEnvironmentIds = (action as DeployLatestReleaseActionResource).SourceEnvironmentIds || [];
                destinationEnvironmentId = [(action as DeployLatestReleaseActionResource).DestinationEnvironmentId];
                break;
            case TriggerActionType.DeployNewRelease:
                preText = "Create a new release";
                postText = " and deploy ";
                destinationEnvironmentId = [(action as DeployNewReleaseActionResource).EnvironmentId];
                break;
            case TriggerActionType.DeployLatestReleaseToEnvironment:
                preText = "Deploy the latest release to environment";
                const envId = (action as DeployLatestReleaseToEnvironmentActionResource).EnvironmentId;
                destinationEnvironmentId = envId ? [envId] : [];
                break;
            case TriggerActionType.RunRunbook:
                if (isRunRunbookActionResource(action)) {
                    if (action.RunbookId) {
                        preText = <>Run Runbook {this.lookupRunbookChip(action.RunbookId, runbooks)}</>;
                    }
                    else if (action.RunbookTags && action.RunbookTags.length > 0) {
                        const hydratedTags = hydrateTagsByName(tagSets, action.RunbookTags).filter((item) => item !== null);
                        preText = (<>
                                Run runbooks matching{" "}
                                {hydratedTags.map((hydratedTag) => (<Tag key={hydratedTag.tag.Id} tagName={hydratedTag.tag.Name} tagColor={hydratedTag.tag.Color} description={hydratedTag.tag.Description}/>))}
                            </>);
                    }
                    else {
                        preText = <>Run Runbook</>;
                    }
                    destinationEnvironmentId = action.EnvironmentIds;
                    to = "in";
                }
                break;
        }
        if ("ChannelId" in action && action.ChannelId) {
            channelDescription = channels.find((c) => c.Id === action.ChannelId) ? <span> in {channelChipList(channels, [action.ChannelId])}</span> : <span> in {<ChannelChip channelName={action.ChannelId}/>}</span>;
        }
        return (<span>
                {preText}
                {channelDescription}
                {postText}
                {sourceEnvironmentIds.length > 0 && <span> from {environmentChipListIncludingMissing(environments, sourceEnvironmentIds)}</span>}
                <span>
                    {" "}
                    {to} {environmentChipListIncludingMissing(environments, destinationEnvironmentId!)}
                </span>
            </span>);
    }
    public static lookupRunbookChip = (id: string, runbooks: RunbookResource[]) => {
        const LookupRunbookChip = LookupResourceChipComponent<RunbookResource>();
        return <LookupRunbookChip lookupCollection={runbooks} key={id} lookupId={id} type={ChipIcon.Runbook} chipRender={(item) => <RunbookChip runbook={item}/>}/>;
    };
    public static getScheduleIntervalDescription(interval: TriggerScheduleIntervalResource, emptyText: string = "") {
        const scheduleIntervalDescription = new ScheduleIntervalDescriptionHelper(interval);
        return scheduleIntervalDescription.getSummary(emptyText);
    }
    private static getDailyScheduleDescription(value: OnceDailyTriggerScheduleResource | ContinuousDailyTriggerScheduleResource) {
        const onceDailySchedule = value.FilterType === TriggerFilterType.OnceDailySchedule ? (value as OnceDailyTriggerScheduleResource) : null;
        const continuousDailySchedule = value.FilterType === TriggerFilterType.ContinuousDailySchedule ? (value as ContinuousDailyTriggerScheduleResource) : null;
        const daysPerWeekDescription = new DaysDescriptionHelper(value).getSummary();
        return (<span className={this.styles.description}>
                {onceDailySchedule && this.getStartTimeDescription(onceDailySchedule.StartTime)}
                {continuousDailySchedule && this.getIntervalDescription(continuousDailySchedule.Interval, continuousDailySchedule.HourInterval, continuousDailySchedule.MinuteInterval)}
                {continuousDailySchedule && this.getRunAfterRunUntilDescription(continuousDailySchedule.RunAfter, continuousDailySchedule.RunUntil)}
                {daysPerWeekDescription}
            </span>);
    }
    private static styles = {
        description: css({ lineHeight: space[24] }),
    };
    private static getDaysPerMonthScheduleDescription(schedule: DaysPerMonthTriggerScheduleResource, emptyText: string) {
        const daysPerMonthDescription = new DaysPerMonthDescriptionHelper(schedule).getSummary(emptyText);
        return (<span>
                {daysPerMonthDescription}
                {this.getStartTimeDescription(schedule.StartTime)}
            </span>);
    }
    private static getCronExpressionScheduleDescription(schedule: CronTriggerScheduleResource, emptyText: string) {
        let cronMeaning = getCronExpressionDescription(schedule.CronExpression);
        cronMeaning = cronMeaning.charAt(0).toLowerCase() + cronMeaning.slice(1);
        return cronMeaning;
    }
    private static getIntervalDescription(intervalType: TriggerScheduleIntervalType, hourInterval?: number, minuteInterval?: number) {
        let intervalDescription = null;
        switch (intervalType) {
            case TriggerScheduleIntervalType.OnceDaily:
                intervalDescription = " once daily";
                break;
            case TriggerScheduleIntervalType.OnceHourly:
                intervalDescription = (<span>
                        {" "}
                        every {hourInterval} {hourInterval! > 1 ? "hours" : "hour"} daily
                    </span>);
                break;
            case TriggerScheduleIntervalType.OnceEveryMinute:
                intervalDescription = (<span>
                        {" "}
                        every {minuteInterval} {minuteInterval! > 1 ? "minutes" : "minute"} daily
                    </span>);
                break;
        }
        return intervalDescription;
    }
    private static getStartTimeDescription(startTime?: Date) {
        return (<span>
                <span> starting at {this.getFormattedTime(startTime!)}</span>
            </span>);
    }
    private static getRunAfterRunUntilDescription(runAfter?: Date, runUntil?: Date) {
        return (<>
                <span>
                    <span> running after {this.getFormattedTime(runAfter!)}</span>
                </span>
                <span>
                    <span> running until {this.getFormattedTime(runUntil!)}</span>
                </span>
            </>);
    }
    private static getFormattedTime(time: Date) {
        return DateFormatter.dateToCustomFormat(moment(time).utc(), "hh:mm A");
    }
}
function isRunRunbookActionResource(action: ScopedDeploymentActionResource | RunRunbookActionResource): action is RunRunbookActionResource {
    return action.ActionType === TriggerActionType.RunRunbook;
}
